/* __next_internal_client_entry_do_not_use__ SDUICarousel auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { SDUIOnViewActionsHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/SDUIOnViewActionHandler";
import { logLegacyViewEvent } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/EventLogging/logLegacyViewEvent";
import { logSDUIEvent } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import { SDUIProductTileImpressionProvider } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIProductTileImpressionProvider";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { ActionLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUITileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/SDUITileRenderer";
import { useConductorComponentDismissal } from "@faire/retailer-visitor-shared/features/Conductor/useConductorComponentDismissal";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { useRecordMilestoneByDay } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { QualtricsRetailerEvent } from "@faire/web--source/common/in-product-survey/qualtrics";
import { triggerQualtricsEvent } from "@faire/web--source/common/in-product-survey/qualtrics";
import { Carousel, ITEM_PER_SLIDE_DEFAULT } from "@faire/web--source/slate-labs/Carousel/Carousel";
import { Column } from "@faire/web--source/slate/Layout";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { ICarouselStyle } from "@faire/web-api--source/indigofair/data/ICarouselStyle";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { getCarouselControlsVariant, getCarouselDragFree, getCarouselLoop, getCarouselShowNavigationArrows, getCarouselTileSpacing } from "@faire/retailer/components/SDUI/ComponentRenderers/Carousel/__internal__/carouselStyleUtils";
var DEFAULT_SDUI_CAROUSEL_PEEK_NEXT_PERCENT = {
    mobileAndAbove: 20,
    tabletAndAbove: 0
};
export var SDUICarousel = function(param) {
    var tiles = param.tiles, style = param.style, _param_numberOfRows = param.numberOfRows, numberOfRows = _param_numberOfRows === void 0 ? 1 : _param_numberOfRows, loggingData = param.loggingData, renderBehaviour = param.renderBehaviour;
    var history = useHistory();
    var recordMilestoneOnceByDay = useRecordMilestoneByDay();
    var clearAllFilters = useFilterStore([]).clearAllFilters;
    var maybeAssignCategoryTreeExperiment = useMaybeAssignCategoryTreeExperiment();
    var dismissSDUIConductorComponent = useConductorComponentDismissal().dismissSDUIConductorComponent;
    var onVisibleCallback = function(newVisibleColumns) {
        var items = newVisibleColumns.flat();
        var actionContent = items.map(function(item) {
            var _item_logging_data;
            return (_item_logging_data = item.logging_data) === null || _item_logging_data === void 0 ? void 0 : _item_logging_data.instance_identifier;
        }).join(" ");
        var actionPositions = items.map(function(item) {
            var _item_logging_data;
            return (_item_logging_data = item.logging_data) === null || _item_logging_data === void 0 ? void 0 : _item_logging_data.tile_position;
        }).join(" ");
        items.forEach(function(item) {
            var _item_on_view;
            SDUIOnViewActionsHandler((_item_on_view = item.on_view) !== null && _item_on_view !== void 0 ? _item_on_view : [], loggingData, history, recordMilestoneOnceByDay, clearAllFilters, maybeAssignCategoryTreeExperiment, dismissSDUIConductorComponent);
        });
        logSDUIEvent("VIEW", ActionLoggingEventParams.build(_object_spread_props(_object_spread({}, loggingData), {
            action_content: actionContent,
            action_positions: actionPositions
        })));
        if (loggingData.view_display_page) {
            var _loggingData_component_name, _loggingData_request_id;
            // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
            // logLegacyViewEvent should be removed once banners and grids are fully migrated to SDUI
            // and data pipelines and dashboards no longer need the layout element events
            logLegacyViewEvent(loggingData.view_display_page, {
                componentName: (_loggingData_component_name = loggingData.component_name) !== null && _loggingData_component_name !== void 0 ? _loggingData_component_name : "",
                requestId: (_loggingData_request_id = loggingData.request_id) !== null && _loggingData_request_id !== void 0 ? _loggingData_request_id : "",
                actionContent: actionContent
            });
            // TODO: https://fairewholesale.atlassian.net/browse/FD-251821
            // clean up after Discovery sub-jobs measurement survey is complete
            if (loggingData.view_display_page === DisplayPageType.HOMEPAGE && (loggingData.component_type === "PRODUCT_TILE_CAROUSEL" || loggingData.component_type === "SMALL_MAKER_TILE_CAROUSEL") && !!actionPositions && !actionPositions.startsWith("1 ")) {
                triggerQualtricsEvent(QualtricsRetailerEvent.clickOnHomepageCarousel);
            }
        }
    };
    var _ref = style !== null && style !== void 0 ? style : ICarouselStyle.build(), loop_behavior = _ref.loop_behavior, navigation_control = _ref.navigation_control, tile_spacing = _ref.tile_spacing, transition_style = _ref.transition_style, _ref_tile_width = _ref.tile_width, tile_width = _ref_tile_width === void 0 ? ICarouselStyle.ITileWidth.build() : _ref_tile_width;
    var fractional = tile_width.fractional;
    var responsiveTilesPerSlide = (fractional === null || fractional === void 0 ? void 0 : fractional.responsive_tiles_per_slide) ? convertServerResponsivePropertyToClientResponsiveProperty(ITEM_PER_SLIDE_DEFAULT.mobileAndAbove, function(value) {
        return Math.floor(value);
    }, fractional === null || fractional === void 0 ? void 0 : fractional.responsive_tiles_per_slide) : undefined;
    var responsivePeekNextPercent = (fractional === null || fractional === void 0 ? void 0 : fractional.responsive_tiles_per_slide) ? convertServerResponsivePropertyToClientResponsiveProperty(ITEM_PER_SLIDE_DEFAULT.mobileAndAbove, function(value) {
        return Math.round(value % 1 * 100);
    }, fractional === null || fractional === void 0 ? void 0 : fractional.responsive_tiles_per_slide) : DEFAULT_SDUI_CAROUSEL_PEEK_NEXT_PERCENT;
    var isFreeFlow = (tile_width === null || tile_width === void 0 ? void 0 : tile_width.content) !== undefined;
    var chunkedTiles = useMemo(function() {
        return chunkTiles(tiles, numberOfRows);
    }, [
        tiles,
        numberOfRows
    ]);
    return /*#__PURE__*/ _jsx(SDUIProductTileImpressionProvider, {
        loggingData: loggingData,
        children: /*#__PURE__*/ _jsx(Carousel, {
            items: chunkedTiles,
            itemsPerSlide: responsiveTilesPerSlide,
            peekNextPercent: responsivePeekNextPercent,
            renderItem: function(tilesColumn) {
                if (tilesColumn.length > 1) {
                    return /*#__PURE__*/ _jsx(Column, {
                        className: "gap-4",
                        children: tilesColumn.map(function(tile) {
                            var _tile_logging_data;
                            return /*#__PURE__*/ _jsx(SDUITileRenderer, {
                                tileElement: tile,
                                loggingData: loggingData
                            }, (_tile_logging_data = tile.logging_data) === null || _tile_logging_data === void 0 ? void 0 : _tile_logging_data.instance_identifier);
                        })
                    });
                } else if (tilesColumn[0]) {
                    return /*#__PURE__*/ _jsx(SDUITileRenderer, {
                        tileElement: tilesColumn[0],
                        loggingData: loggingData
                    });
                }
                return null;
            },
            itemGap: getCarouselTileSpacing(tile_spacing),
            loop: getCarouselLoop(loop_behavior),
            dragFree: getCarouselDragFree(transition_style),
            showNavigationArrows: getCarouselShowNavigationArrows(navigation_control),
            arrowColorOrVariant: getCarouselControlsVariant(navigation_control),
            shouldShowDisabledNavigationArrows: true,
            onVisible: onVisibleCallback,
            freeFlow: isFreeFlow,
            arrowOffsetX: 0,
            attachRefOnView: renderBehaviour === null || renderBehaviour === void 0 ? void 0 : renderBehaviour.deferInitializationUntilView
        })
    });
};
var chunkTiles = function(tiles, numberOfRows) {
    return tiles.reduce(function(arr, item, idx) {
        var _arr_slice_;
        return idx % numberOfRows === 0 ? _to_consumable_array(arr).concat([
            [
                item
            ]
        ]) : _to_consumable_array(arr.slice(0, -1)).concat([
            _to_consumable_array((_arr_slice_ = arr.slice(-1)[0]) !== null && _arr_slice_ !== void 0 ? _arr_slice_ : []).concat([
                item
            ])
        ]);
    }, []);
};
